import { useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Star, CheckCircle } from 'lucide-react'
import { apiFetch, ApiClientError } from '@/lib/api-client'

// ─── Types ───────────────────────────────────────────────────────────────────

interface TestimonialRequestDetails {
  id: string
  crewMemberName: string
  claimedRole: string
  eventName: string | null
  recipientName: string | null
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function TestimonialFormPage() {
  const { token } = useParams<{ token: string }>()

  const { data, isLoading, error } = useQuery<TestimonialRequestDetails, ApiClientError>({
    queryKey: ['testimonial-request', token],
    queryFn: () => apiFetch<TestimonialRequestDetails>(`/public/testimonial/${token}`),
    retry: false,
  })

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ovation-paper">
        <p className="text-sm text-ovation-muted">Loading...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ovation-paper">
        <div className="w-full max-w-md rounded-lg border border-red-200 bg-white p-8 text-center">
          <p className="text-sm text-red-700">
            {error.message ?? 'This testimonial link is invalid or has expired.'}
          </p>
        </div>
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="flex min-h-screen items-center justify-center bg-ovation-paper p-4">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-ovation-ink">Leave a Testimonial</h1>
          <p className="mt-2 text-sm text-ovation-muted">
            {data.crewMemberName} has asked you to verify their work
            {data.eventName ? ` on "${data.eventName}"` : ''}.
          </p>
        </div>
        <TestimonialFormBody request={data} token={token!} />
      </div>
    </div>
  )
}

// ─── Form ────────────────────────────────────────────────────────────────────

function TestimonialFormBody({
  request,
  token,
}: {
  request: TestimonialRequestDetails
  token: string
}) {
  const [fromName, setFromName] = useState(request.recipientName ?? '')
  const [fromRole, setFromRole] = useState('')
  const [roleConfirmed, setRoleConfirmed] = useState<boolean | null>(null)
  const [actualRole, setActualRole] = useState('')
  const [rating, setRating] = useState(0)
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (roleConfirmed === null || rating === 0) return
    setError(null)
    setSubmitting(true)
    try {
      const body: Record<string, unknown> = {
        fromName,
        fromRole,
        roleConfirmed,
        rating,
        text,
      }
      if (!roleConfirmed && actualRole) {
        body.actualRole = actualRole
      }
      await apiFetch(`/public/testimonial/${token}`, {
        method: 'POST',
        body,
        skipAuth: true,
      })
      setSubmitted(true)
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to submit')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="rounded-lg border border-green-200 bg-white p-8 text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
        <h2 className="mt-4 text-lg font-semibold text-ovation-ink">Thank you!</h2>
        <p className="mt-2 text-sm text-ovation-muted">
          Your testimonial has been submitted successfully.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 rounded-lg border border-ovation-ink/10 bg-white p-6">
      {/* Crew member info */}
      <div className="rounded-md bg-ovation-paper/50 p-4">
        <p className="text-sm">
          <span className="font-medium">{request.crewMemberName}</span> claims
          they performed the role of{' '}
          <span className="font-medium">{request.claimedRole}</span>
          {request.eventName && (
            <span> on <span className="font-medium">{request.eventName}</span></span>
          )}
          .
        </p>
      </div>

      {/* Your details */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="tf-name" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Your name *
          </label>
          <input
            id="tf-name"
            type="text"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            required
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="tf-role" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
            Your role/position *
          </label>
          <input
            id="tf-role"
            type="text"
            value={fromRole}
            onChange={(e) => setFromRole(e.target.value)}
            required
            placeholder="e.g. Tour Manager, Venue Owner"
            className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
          />
        </div>
      </div>

      {/* Role confirmation */}
      <div>
        <p className="mb-2 text-sm font-medium">
          Did {request.crewMemberName} perform the role of &ldquo;{request.claimedRole}&rdquo;?
        </p>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="roleConfirmed"
              checked={roleConfirmed === true}
              onChange={() => setRoleConfirmed(true)}
              className="h-4 w-4"
            />
            Yes
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="roleConfirmed"
              checked={roleConfirmed === false}
              onChange={() => setRoleConfirmed(false)}
              className="h-4 w-4"
            />
            No
          </label>
        </div>
        {roleConfirmed === false && (
          <div className="mt-3">
            <label htmlFor="tf-actual-role" className="mb-1 block text-xs text-ovation-muted">
              What role did they actually perform?
            </label>
            <input
              id="tf-actual-role"
              type="text"
              value={actualRole}
              onChange={(e) => setActualRole(e.target.value)}
              className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
            />
          </div>
        )}
      </div>

      {/* Star rating */}
      <div>
        <label className="mb-2 block text-xs uppercase tracking-wider text-ovation-muted">
          Rating *
        </label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              className="rounded p-1 hover:bg-ovation-ink/5"
            >
              <Star
                className={`h-7 w-7 ${
                  n <= rating
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-ovation-ink/20'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Testimonial text */}
      <div>
        <label htmlFor="tf-text" className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">
          Testimonial *
        </label>
        <textarea
          id="tf-text"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          required
          placeholder="Describe their work, professionalism, and contribution..."
          className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm focus:border-ovation-ink focus:outline-none"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting || roleConfirmed === null || rating === 0 || !text.trim() || !fromName.trim() || !fromRole.trim()}
        className="w-full rounded-md bg-ovation-ink px-4 py-2.5 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
      >
        {submitting ? 'Submitting...' : 'Submit Testimonial'}
      </button>
    </form>
  )
}
